/**
 * IndexedDB Cache Layer for Media Files
 * 
 * Persistent storage layer using IndexedDB for caching media files
 * across browser sessions. Provides larger storage capacity than
 * memory cache with automatic TTL-based expiration.
 */

const DB_NAME = 'infiniteCanvasMediaCache';
const DB_VERSION = 1;
const STORE_NAME = 'mediaCache';

export interface IndexedDBCacheEntry {
    key: string;            // Cache key (media reference)
    data: string;           // Base64 data URL
    contentType: string;    // MIME type
    size: number;           // Size in bytes
    createdAt: number;      // When entry was created
    lastAccessed: number;   // When entry was last accessed
    expiresAt: number;      // TTL expiration timestamp
}

export interface IndexedDBCacheConfig {
    maxSize: number;        // Maximum total cache size in bytes
    defaultTTL: number;     // Default TTL in milliseconds
}

const DEFAULT_CONFIG: IndexedDBCacheConfig = {
    maxSize: 500 * 1024 * 1024,     // 500MB
    defaultTTL: 7 * 24 * 60 * 60 * 1000,  // 7 days
};

class IndexedDBCache {
    private db: IDBDatabase | null = null;
    private config: IndexedDBCacheConfig;
    private initPromise: Promise<void> | null = null;

    constructor(config: Partial<IndexedDBCacheConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
    }

    /**
     * Initialize the IndexedDB database
     */
    private async init(): Promise<void> {
        if (this.db) return;
        
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = new Promise((resolve, reject) => {
            if (!('indexedDB' in window)) {
                console.warn('[IndexedDBCache] IndexedDB not supported');
                reject(new Error('IndexedDB not supported'));
                return;
            }

            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('[IndexedDBCache] Failed to open database:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('[IndexedDBCache] Database initialized');
                
                // Schedule periodic cleanup
                this.scheduleCleanup();
                
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                
                // Create object store with key as primary key
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                    
                    // Create indexes for queries
                    store.createIndex('expiresAt', 'expiresAt', { unique: false });
                    store.createIndex('lastAccessed', 'lastAccessed', { unique: false });
                    store.createIndex('createdAt', 'createdAt', { unique: false });
                    store.createIndex('size', 'size', { unique: false });
                    
                    console.log('[IndexedDBCache] Object store created');
                }
            };
        });

        return this.initPromise;
    }

    /**
     * Get item from cache
     */
    async get(key: string): Promise<string | null> {
        try {
            await this.init();
            if (!this.db) return null;

            return new Promise((resolve, reject) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(key);

                request.onsuccess = () => {
                    const entry: IndexedDBCacheEntry | undefined = request.result;
                    
                    if (!entry) {
                        console.log(`[IndexedDBCache] MISS: ${key}`);
                        resolve(null);
                        return;
                    }

                    // Check if expired
                    if (entry.expiresAt < Date.now()) {
                        console.log(`[IndexedDBCache] EXPIRED: ${key}`);
                        this.delete(key); // Async cleanup
                        resolve(null);
                        return;
                    }

                    // Update last accessed time
                    entry.lastAccessed = Date.now();
                    store.put(entry);

                    console.log(`[IndexedDBCache] HIT: ${key} (${(entry.size / 1024).toFixed(2)} KB)`);
                    resolve(entry.data);
                };

                request.onerror = () => {
                    console.error('[IndexedDBCache] Get failed:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('[IndexedDBCache] Get error:', error);
            return null;
        }
    }

    /**
     * Set item in cache
     */
    async set(key: string, data: string, contentType: string = 'application/octet-stream', ttl?: number): Promise<void> {
        try {
            await this.init();
            if (!this.db) return;

            const size = this.calculateSize(data);
            const now = Date.now();

            // Check if item exceeds max size
            if (size > this.config.maxSize) {
                console.warn(`[IndexedDBCache] Item too large: ${key} (${(size / 1024 / 1024).toFixed(2)} MB)`);
                return;
            }

            // Ensure we have space
            await this.ensureSpace(size);

            const entry: IndexedDBCacheEntry = {
                key,
                data,
                contentType,
                size,
                createdAt: now,
                lastAccessed: now,
                expiresAt: now + (ttl || this.config.defaultTTL),
            };

            return new Promise((resolve, reject) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.put(entry);

                request.onsuccess = () => {
                    console.log(`[IndexedDBCache] SET: ${key} (${(size / 1024).toFixed(2)} KB, TTL: ${(ttl || this.config.defaultTTL) / 1000 / 60 / 60}h)`);
                    resolve();
                };

                request.onerror = () => {
                    console.error('[IndexedDBCache] Set failed:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('[IndexedDBCache] Set error:', error);
        }
    }

    /**
     * Check if key exists in cache
     */
    async has(key: string): Promise<boolean> {
        try {
            await this.init();
            if (!this.db) return false;

            return new Promise((resolve) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(key);

                request.onsuccess = () => {
                    const entry: IndexedDBCacheEntry | undefined = request.result;
                    if (!entry || entry.expiresAt < Date.now()) {
                        resolve(false);
                    } else {
                        resolve(true);
                    }
                };

                request.onerror = () => resolve(false);
            });
        } catch {
            return false;
        }
    }

    /**
     * Delete item from cache
     */
    async delete(key: string): Promise<boolean> {
        try {
            await this.init();
            if (!this.db) return false;

            return new Promise((resolve, reject) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.delete(key);

                request.onsuccess = () => {
                    console.log(`[IndexedDBCache] DELETE: ${key}`);
                    resolve(true);
                };

                request.onerror = () => {
                    console.error('[IndexedDBCache] Delete failed:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('[IndexedDBCache] Delete error:', error);
            return false;
        }
    }

    /**
     * Clear entire cache
     */
    async clear(): Promise<void> {
        try {
            await this.init();
            if (!this.db) return;

            return new Promise((resolve, reject) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.clear();

                request.onsuccess = () => {
                    console.log('[IndexedDBCache] CLEARED');
                    resolve();
                };

                request.onerror = () => {
                    console.error('[IndexedDBCache] Clear failed:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('[IndexedDBCache] Clear error:', error);
        }
    }

    /**
     * Get cache statistics
     */
    async getStats(): Promise<{
        entries: number;
        totalSize: number;
        maxSize: number;
        utilizationPercent: number;
        oldestEntry: number | null;
        newestEntry: number | null;
    }> {
        try {
            await this.init();
            if (!this.db) {
                return { entries: 0, totalSize: 0, maxSize: this.config.maxSize, utilizationPercent: 0, oldestEntry: null, newestEntry: null };
            }

            return new Promise((resolve, reject) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.getAll();

                request.onsuccess = () => {
                    const entries: IndexedDBCacheEntry[] = request.result;
                    const totalSize = entries.reduce((sum, e) => sum + e.size, 0);
                    const timestamps = entries.map(e => e.createdAt);

                    resolve({
                        entries: entries.length,
                        totalSize,
                        maxSize: this.config.maxSize,
                        utilizationPercent: (totalSize / this.config.maxSize) * 100,
                        oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : null,
                        newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : null,
                    });
                };

                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            return { entries: 0, totalSize: 0, maxSize: this.config.maxSize, utilizationPercent: 0, oldestEntry: null, newestEntry: null };
        }
    }

    /**
     * Calculate size of base64 data
     */
    private calculateSize(data: string): number {
        const base64Part = data.replace(/^data:[^;]+;base64,/, '');
        return Math.floor((base64Part.length * 3) / 4);
    }

    /**
     * Ensure there's enough space for new entry
     */
    private async ensureSpace(requiredSize: number): Promise<void> {
        const stats = await this.getStats();
        
        if (stats.totalSize + requiredSize <= this.config.maxSize) {
            return; // Already have space
        }

        // Need to evict entries - start with least recently accessed
        await this.evictLRU(stats.totalSize + requiredSize - this.config.maxSize);
    }

    /**
     * Evict least recently used entries to free up space
     */
    private async evictLRU(bytesToFree: number): Promise<void> {
        try {
            await this.init();
            if (!this.db) return;

            return new Promise((resolve, reject) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const index = store.index('lastAccessed');
                const request = index.openCursor();
                
                let freedBytes = 0;

                request.onsuccess = (event) => {
                    const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                    
                    if (cursor && freedBytes < bytesToFree) {
                        const entry: IndexedDBCacheEntry = cursor.value;
                        freedBytes += entry.size;
                        console.log(`[IndexedDBCache] EVICT (LRU): ${entry.key} (${(entry.size / 1024).toFixed(2)} KB)`);
                        cursor.delete();
                        cursor.continue();
                    } else {
                        console.log(`[IndexedDBCache] Evicted ${(freedBytes / 1024).toFixed(2)} KB`);
                        resolve();
                    }
                };

                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('[IndexedDBCache] Eviction error:', error);
        }
    }

    /**
     * Remove expired entries
     */
    async cleanupExpired(): Promise<number> {
        try {
            await this.init();
            if (!this.db) return 0;

            // Check if IDBKeyRange is available (not in Node.js/jsdom)
            if (typeof IDBKeyRange === 'undefined') {
                console.warn('[IndexedDBCache] IDBKeyRange not available, skipping cleanup');
                return 0;
            }

            return new Promise((resolve, reject) => {
                const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const index = store.index('expiresAt');
                const range = IDBKeyRange.upperBound(Date.now());
                const request = index.openCursor(range);
                
                let count = 0;

                request.onsuccess = (event) => {
                    const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                    
                    if (cursor) {
                        cursor.delete();
                        count++;
                        cursor.continue();
                    } else {
                        if (count > 0) {
                            console.log(`[IndexedDBCache] Cleaned up ${count} expired entries`);
                        }
                        resolve(count);
                    }
                };

                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('[IndexedDBCache] Cleanup error:', error);
            return 0;
        }
    }

    /**
     * Schedule periodic cleanup
     */
    private scheduleCleanup(): void {
        // Run cleanup every hour
        setInterval(() => {
            this.cleanupExpired();
        }, 60 * 60 * 1000);

        // Also run cleanup on init
        this.cleanupExpired();
    }
}

// Singleton instance
export const indexedDBCache = new IndexedDBCache();

// Export class for testing/custom instances
export { IndexedDBCache };
